import type { CSSProperties } from "react";
import { createTheme } from "@mui/material";
import type {
  Components,
  Shadows,
  Theme,
  ThemeOptions,
  TypographyVariantsOptions,
} from "@mui/material/styles";

import Inter from "@/ui/assets/fonts/Inter-VariableFont.ttf";

import "@/ui/mui/augmentation";
import "@mui/x-data-grid-pro/themeAugmentation";

export const defaultTheme = createTheme();
export type TSCTheme = ReturnType<typeof createTheme>;

const shadows: Shadows = [
  // elevation 0
  "none",
  // elevation 1
  "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px;",
  // elevation 2
  "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px;",
  // elevation 3
  "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px;",
  // elevation 4
  "rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px;",
  // elevation 5
  "rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px;",
  // elevation 6
  "rgba(0, 0, 0, 0.1) 0px 20px 25px -5px, rgba(0, 0, 0, 0.04) 0px 10px 10px -5px;",
  // elevation 7
  "rgba(0, 0, 0, 0.1) 0px 20px 25px -5px, rgba(0, 0, 0, 0.04) 0px 10px 10px -5px;",
  // elevation 8
  "rgba(0, 0, 0, 0.1) 0px 20px 25px -5px, rgba(0, 0, 0, 0.04) 0px 10px 10px -5px;",
  "0px 5px 6px -3px rgba(0,0,0,0.2), 0px 9px 12px 1px rgba(0,0,0,0.14), 0px 3px 16px 2px rgba(0,0,0,0.12)",
  "0px 6px 6px -3px rgba(0,0,0,0.2), 0px 10px 14px 1px rgba(0,0,0,0.14), 0px 4px 18px 3px rgba(0,0,0,0.12)",
  "0px 6px 7px -4px rgba(0,0,0,0.2), 0px 11px 15px 1px rgba(0,0,0,0.14), 0px 4px 20px 3px rgba(0,0,0,0.12)",
  "0px 7px 8px -4px rgba(0,0,0,0.2), 0px 12px 17px 2px rgba(0,0,0,0.14), 0px 5px 22px 4px rgba(0,0,0,0.12)",
  "0px 7px 8px -4px rgba(0,0,0,0.2), 0px 13px 19px 2px rgba(0,0,0,0.14), 0px 5px 24px 4px rgba(0,0,0,0.12)",
  "0px 7px 9px -4px rgba(0,0,0,0.2), 0px 14px 21px 2px rgba(0,0,0,0.14), 0px 5px 26px 4px rgba(0,0,0,0.12)",
  "0px 8px 9px -5px rgba(0,0,0,0.2), 0px 15px 22px 2px rgba(0,0,0,0.14), 0px 6px 28px 5px rgba(0,0,0,0.12)",
  "0px 8px 10px -5px rgba(0,0,0,0.2), 0px 16px 24px 2px rgba(0,0,0,0.14), 0px 6px 30px 5px rgba(0,0,0,0.12)",
  "0px 8px 11px -5px rgba(0,0,0,0.2), 0px 17px 26px 2px rgba(0,0,0,0.14), 0px 6px 32px 5px rgba(0,0,0,0.12)",
  "0px 9px 11px -5px rgba(0,0,0,0.2), 0px 18px 28px 2px rgba(0,0,0,0.14), 0px 7px 34px 6px rgba(0,0,0,0.12)",
  "0px 9px 12px -6px rgba(0,0,0,0.2), 0px 19px 29px 2px rgba(0,0,0,0.14), 0px 7px 36px 6px rgba(0,0,0,0.12)",
  "0px 10px 13px -6px rgba(0,0,0,0.2), 0px 20px 31px 3px rgba(0,0,0,0.14), 0px 8px 38px 7px rgba(0,0,0,0.12)",
  "0px 10px 13px -6px rgba(0,0,0,0.2), 0px 21px 33px 3px rgba(0,0,0,0.14), 0px 8px 40px 7px rgba(0,0,0,0.12)",
  "0px 10px 14px -6px rgba(0,0,0,0.2), 0px 22px 35px 3px rgba(0,0,0,0.14), 0px 8px 42px 7px rgba(0,0,0,0.12)",
  "0px 11px 14px -7px rgba(0,0,0,0.2), 0px 23px 36px 3px rgba(0,0,0,0.14), 0px 9px 44px 8px rgba(0,0,0,0.12)",
  "0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)",
];

// elevation used: 1 4 5 8

const BORDER_RADIUS = {
  small: 4,
  medium: 8,
  large: 12,
};

const shape: ThemeOptions["shape"] = {
  borderRadius: BORDER_RADIUS.medium,
};

export const typography: TypographyVariantsOptions = {
  fontFamily: "Cera",
  fontSize: 14,
  "body1-bold": {
    fontSize: "1rem",
    fontWeight: 700,
    lineHeight: 1.5,
    letterSpacing: "0.00938em",
  },
  "body2-bold": {
    fontSize: "0.875rem",
    fontWeight: 700,
    lineHeight: 1.43,
    letterSpacing: "0.01071em",
  },
  "h6-bold": {
    fontSize: "1.25rem",
    fontWeight: 700,
    lineHeight: 1.6,
    letterSpacing: "0.0075em",
  },
  h7: {
    fontSize: "1rem",
    fontWeight: 700,
    lineHeight: 1.6,
    letterSpacing: "0.00938em",
  },
};

const TICK_ICON: CSSProperties = {
  content: '""',
  position: "absolute",
  right: 8,
  top: "50%",
  transform: "translateY(-50%)",
  width: "20px",
  height: "20px",
  backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg width='20' stroke='%2300b5fe' stroke-width='1.67' stroke-linecap='round' stroke-linejoin='round' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M16.25 6.875L8.125 15L3.75 10.625'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  backgroundSize: "contain",
};

export const components: Components<Omit<Theme, "components">> = {
  MuiCssBaseline: {
    styleOverrides: `
      @font-face {
        font-family: 'Inter';
        font-weight: 100 900;
        src: url(${Inter}) format('truetype-variations');
      }
    `,
  },
  MuiInput: {
    styleOverrides: {
      underline: ({ ownerState }) => ({
        ...(ownerState.readOnly
          ? {
              ":before": {
                display: "none",
              },
              ":after": {
                display: "none",
              },
            }
          : {
              ":before": {
                borderBottom: "2px solid rgba(0, 0, 0, 0)",
              },
            }),
      }),
    },
  },
  MuiInputLabel: {
    defaultProps: {
      size: "small",
    },
  },
  MuiInputBase: {
    defaultProps: {
      size: "small",
    },
    styleOverrides: {
      root: {
        "&.Mui-disabled": {
          backgroundColor: "rgba(0, 0, 0, 0.06)",
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: BORDER_RADIUS.medium,
        "&.Mui-disabled": {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(0, 0, 0, 0.06)",
          },
        },
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      InputLabelProps: {
        shrink: true,
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      standard: {
        backgroundColor: "transparent",
      },
    },
  },
  MuiAutocomplete: {
    defaultProps: {
      autoComplete: true,
    },
    styleOverrides: {
      listbox: {
        padding: 8,
        "& .MuiAutocomplete-option": {
          borderRadius: BORDER_RADIUS.medium,
          "&[aria-selected='true']": {
            position: "relative",
            paddingRight: 36,
            "&::after": TICK_ICON,
          },
        },
      },
      paper: {
        marginTop: 4,
        marginBottom: 4,
      },
      option: {
        marginTop: 1,
        marginBottom: 1,
        pl: 16,
      },
      groupUl: {
        "& .MuiAutocomplete-option": {
          paddingLeft: 16,
        },
      },
    },
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        textTransform: "capitalize",
        whiteSpace: "nowrap",
        borderRadius: BORDER_RADIUS.large,
      },
      sizeSmall: {
        borderRadius: BORDER_RADIUS.medium,
      },
    },
  },
  MuiFab: {
    styleOverrides: {
      root: {
        textTransform: "capitalize",
      },
    },
  },
  MuiLink: {
    defaultProps: {
      underline: "hover",
    },
  },
  MuiStack: {
    defaultProps: {
      useFlexGap: true,
    },
  },
  MuiTabs: {
    defaultProps: {
      textColor: "secondary",
      indicatorColor: "secondary",
    },
    styleOverrides: {
      root: {
        minHeight: 42,
      },
    },
  },
  MuiTab: {
    defaultProps: {
      iconPosition: "start",
    },
    styleOverrides: {
      root: ({ ownerState }) => {
        return {
          minHeight:
            ownerState?.iconPosition === "start" ||
            ownerState?.iconPosition === "end"
              ? 42
              : undefined,
          paddingTop: 9,
          paddingBottom: 9,
          textTransform: "capitalize",
          [`& .MuiTab-iconWrapper.material-symbols-outlined,
            .MuiTab-iconWrapper.material-symbols-rounded,
            .MuiTab-iconWrapper.material-symbols-sharp`]: {
            fontSize: 18,
          },
        };
      },
    },
  },
  MuiTooltip: {
    defaultProps: {
      enterDelay: 10,
      enterTouchDelay: 100,
    },
    styleOverrides: {
      tooltip: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[4],
        color: theme.palette.text.secondary,
        backgroundImage:
          theme.palette.mode === "dark"
            ? "linear-gradient(rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.09))"
            : undefined,
      }),
      arrow: ({ theme }) => ({
        "&:before": {
          backgroundColor: theme.palette.background.paper,
          backgroundImage:
            theme.palette.mode === "dark"
              ? "linear-gradient(rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.09))"
              : undefined,
        },
      }),
    },
  },
  MuiDataGrid: {
    defaultProps: {
      disableColumnMenu: true,
      disableColumnReorder: true,
      disableColumnResize: true,
      hideFooter: true,
      rowSelection: false,
      filterMode: "server",
      rowsLoadingMode: "server",
      sortingMode: "server",
      sortingOrder: ["asc", "desc"],
    },
    styleOverrides: {
      root: {
        border: "none",
      },
      row: {
        ":hover, &.Mui-hovered": {
          backgroundColor: "transparent",
        },
      },
      cell: {
        ":focus": {
          outline: "none",
        },
        ":focus-within": {
          outline: "none",
        },
      },
      columnHeader: {
        ":focus": {
          outline: "none",
        },
        ":focus-within": {
          outline: "none",
        },
      },
      columnSeparator: {
        display: "none",
      },
    },
  },
  MuiDialog: {
    defaultProps: {
      PaperProps: {
        elevation: 8,
      },
    },
  },
  MuiTypography: {
    defaultProps: {
      variantMapping: {
        h7: "div",
      },
    },
  },
  MuiAlert: {
    variants: [
      {
        props: { variant: "outlined", severity: "neutral" },
        style: {
          border: `1px solid ${defaultTheme.palette.divider}`,
        },
      },
    ],
  },
  MuiCheckbox: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        borderRadius: BORDER_RADIUS.small,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      rounded: {
        borderRadius: BORDER_RADIUS.large,
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      list: {
        padding: 8,
      },
      paper: {
        marginTop: 4,
        marginBottom: 4,
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        marginTop: 1,
        marginBottom: 1,
        borderRadius: BORDER_RADIUS.medium,
        "&.Mui-selected": {
          paddingRight: 36,
          "&::after": TICK_ICON,
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
      },
    },
  },
  MuiList: {
    styleOverrides: {
      root: {
        padding: 8,
      },
    },
  },
  MuiBackdrop: {
    styleOverrides: {
      root: {
        backgroundColor: "rgba(12, 17, 29, 0.7)",
        backdropFilter: "blur(8px)",
      },
      invisible: {
        backgroundColor: "transparent",
        backdropFilter: "none",
      },
    },
  },
};

const commonTheme: ThemeOptions = { shadows, typography, components, shape };

export default commonTheme;
